import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { IncadaTenant } from '../../models/incada-tenant';
import { IncadaService } from '../../services/incada.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import * as constants from 'src/app/config/app-constants';

/**
 * Encapsulates logic for an autocomplete control that selects an available Incada tenant.
 * Form value is the tenant name.
 */
@Component({
    selector: 'app-incada-tenant-select',
    templateUrl: './incada-tenant-select.component.html',
    styleUrls: ['./incada-tenant-select.component.scss'],
    standalone: false
})
export class IncadaTenantSelectComponent implements OnInit, OnChanges, OnDestroy {
  /** The parent form this control is a part of. */
  @Input() form: FormGroup;
  /** Name of the form control to bind to on the parent form. */
  @Input() controlName: string;
  /** Determines whether the backing controls are enabled/disabled. */
  @Input() disabled: boolean;
  /** Emits the full tenant object when a valid tenant is selected, or null when cleared. */
  @Output() tenantSelected = new EventEmitter<IncadaTenant | null>();

  /** Subscription to the service call. */
  private dataSubscription: Subscription;
  /** Subscription to value changes. */
  private valueChangeSubscription: Subscription;
  /** Full list of available tenants. */
  tenants: IncadaTenant[] = [];
  /** Filtered list of tenants to display in autocomplete control. */
  filteredTenants: IncadaTenant[] = [];

  /** The form control associated with this component. */
  get tenantControl() { return this.form.get(this.controlName) as FormControl<string>; }

  constructor(
    private incadaService: IncadaService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.dataSubscription = this.incadaService.getActiveTenants().subscribe({
      next: (tenants) => {
        this.tenants = tenants;

        this.tenantControl.addValidators(this.validateTenant().bind(this));

        this.valueChangeSubscription = this.tenantControl.valueChanges.subscribe({
          next: value => {
            this.filteredTenants = this.filterTenants(value || '');

            // Emit the selected tenant when it matches exactly
            const matched = this.tenants.find(t => t.name === value);
            this.tenantSelected.emit(matched || null);
          }
        });

        if (this.tenantControl.value) {
          this.tenantControl.setValue(this.tenantControl.value);
        } else {
          this.filteredTenants = this.filterTenants('');
        }
      },
      error: (err) => {
        console.error(err);
        this.toastService.openToast('Error loading Incada tenant list.', constants.ToastPanelType.error);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && this.tenantControl) {
      this.disabled ? this.tenantControl.disable() : this.tenantControl.enable();
    }
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
  }

  /** Custom validator for tenant form data. */
  validateTenant(): ValidatorFn {
    return (control: AbstractControl<string, string>): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      if (!this.tenants || this.tenants.length === 0) {
        return { NoOptionsError: true };
      }

      if (!this.tenants.find(t => t.name === value)) {
        return { InvalidOptionError: true };
      }

      return null;
    }
  }

  private filterTenants(name: string): IncadaTenant[] {
    if (!name) {
      return this.tenants;
    }

    return this.tenants.filter(t => t.name.toLowerCase().includes(name.toLowerCase()));
  }
}
